import { memo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader } from "ui/components/Loader";
import { NoResults } from "ui/components/NoResults";
import { formatCurrency, formatDateParam, useTranslate } from "helpers";
import { Routes as FinanceRoutes } from "ui/modules/finance/navigation";
import {
  getContractRentStatusesAPI,
  getContractSaleStatusesAPI,
  getFleetVehicleContractRentsAPI,
  getFleetVehicleContractSalesAPI,
} from "services/api/fleet/vehicles/contracts/api";
import { initialPagination } from "ui/components/Table/consts";
import {
  ContractRentStatus,
  ContractSaleStatus,
  FleetVehicleContractRentRow,
  FleetVehicleContractSaleRow,
} from "services/api/fleet/vehicles/contracts/types";
import {
  getContractStatusBarFill,
  getContractStatusRingColor,
  getContractStatusAmountColor,
  contractStatusRank,
} from "services/api/contracts/consts";
import Sparkline from "../../../common/Sparkline";
import DashboardTooltip, { TooltipDivider, TooltipRow, TooltipTitle } from "../../../common/DashboardTooltip";
import BarTooltip, { BarTooltipRow, BarTooltipTitle } from "../../../common/HoverPopup";
import CardHeader from "../../../common/CardHeader";
import { primary, text } from "../../../common/dashboardPalette";
import { ContractStats, ContractStatusCategory } from "./types";
import { useDashboardDateRange } from "../../../../context/DashboardDateRangeContext";
import { inRange } from "services/api/dashboard/range";
import { rangeLabel } from "ui/components/DateRangePicker";

type ContractType = "sale" | "rent";

interface AggregatedContract {
  statusId: string;
  total: number;
  type: ContractType;
}

const ContractStatsTab: React.FC = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { range } = useDashboardDateRange();

  // Klik na status-red -> pretraga ugovora po tom statusu + trenutnom datumskom rasponu widgeta.
  // Landa na tab "Prodaja"; isti `status` param čita i tab "Najam".
  const handleRowClick = (statusId: string) => {
    const params = new URLSearchParams({ tab: "sale", status: statusId });
    const from = formatDateParam(range.from);
    const to = formatDateParam(range.to);
    if (from) params.set("contractDateFrom", from);
    if (to) params.set("contractDateTo", to);
    navigate(`${FinanceRoutes.ContractSearchPageRouter}?${params.toString()}`);
  };

  const [stats, setStats] = useState<ContractStats>({
    totalCount: 0,
    totalValue: 0,
    totalSaleCount: 0,
    totalRentCount: 0,
    totalSaleValue: 0,
    totalRentValue: 0,
    avgValue: 0,
    inProgressCount: 0,
    inProgressValue: 0,
    monthlyTrend: [],
    categories: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchContractStats = async () => {
    try {
      setIsLoading(true);

      const [saleStatusesResult, rentStatusesResult, salesResult, rentsResult] = await Promise.all([
        getContractSaleStatusesAPI(),
        getContractRentStatusesAPI(),
        getFleetVehicleContractSalesAPI({ ...initialPagination, pageSize: 1000 }),
        getFleetVehicleContractRentsAPI({ ...initialPagination, pageSize: 1000 }),
      ]);

      const saleStatuses = saleStatusesResult as ContractSaleStatus[];
      const rentStatuses = rentStatusesResult as ContractRentStatus[];
      // Filtriranje po globalnom datumskom rasponu (contractDate).
      const sales = salesResult.data.filter((c: FleetVehicleContractSaleRow) =>
        inRange(c.contractDate ? new Date(c.contractDate) : null, range));
      const rents = rentsResult.data.filter((c: FleetVehicleContractRentRow) =>
        inRange(c.contractDate ? new Date(c.contractDate) : null, range));

      const aggregated: (AggregatedContract & { date: Date | null })[] = [
        ...sales.map((c: FleetVehicleContractSaleRow) => ({
          statusId: c.status?.id || "unknown",
          total: c.sellingPrice || 0,
          type: "sale" as ContractType,
          date: c.contractDate ? new Date(c.contractDate) : null,
        })),
        ...rents.map((c: FleetVehicleContractRentRow) => ({
          statusId: c.status?.id || "unknown",
          total: c.sellingPrice || 0,
          type: "rent" as ContractType,
          date: c.contractDate ? new Date(c.contractDate) : null,
        })),
      ];

      const totalCount = aggregated.length;
      const totalValue = aggregated.reduce((sum, c) => sum + c.total, 0);
      const totalSaleCount = sales.length;
      const totalRentCount = rents.length;
      const totalSaleValue = sales.reduce((sum: number, c: FleetVehicleContractSaleRow) => sum + (c.sellingPrice || 0), 0);
      const totalRentValue = rents.reduce((sum: number, c: FleetVehicleContractRentRow) => sum + (c.sellingPrice || 0), 0);

      const grouped = aggregated.reduce(
        (acc: Record<string, { count: number; total: number; saleCount: number; rentCount: number; saleTotal: number; rentTotal: number }>, c) => {
          if (!acc[c.statusId]) {
            acc[c.statusId] = { count: 0, total: 0, saleCount: 0, rentCount: 0, saleTotal: 0, rentTotal: 0 };
          }
          acc[c.statusId].count += 1;
          acc[c.statusId].total += c.total;
          if (c.type === "sale") {
            acc[c.statusId].saleCount += 1;
            acc[c.statusId].saleTotal += c.total;
          } else {
            acc[c.statusId].rentCount += 1;
            acc[c.statusId].rentTotal += c.total;
          }
          return acc;
        },
        {}
      );

      // Sale i rent dijele iste statusId-eve (draft/created/signed/invoiced/cancelled),
      // pa label uzima iz prvog match-a (sale ima prioritet, rent kao fallback).
      const findStatus = (id: string) =>
        saleStatuses.find((s) => s.id === id) || rentStatuses.find((s) => s.id === id);

      const categories: ContractStatusCategory[] = Object.entries(grouped)
        .map(([statusId, data]) => {
          const status = findStatus(statusId);
          return {
            statusId,
            label: status?.name || statusId,
            count: data.count,
            total: data.total,
            percentage: totalCount > 0 ? Math.round((data.count / totalCount) * 100) : 0,
            color: status?.color || "blue",
            saleCount: data.saleCount,
            rentCount: data.rentCount,
            saleTotal: data.saleTotal,
            rentTotal: data.rentTotal,
          };
        })
        .sort((a, b) => contractStatusRank(a.statusId) - contractStatusRank(b.statusId));

      // U tijeku = ugovori koji nisu invoiced ni cancelled (draft/created/signed).
      const inProgress = aggregated.filter((c) =>
        c.statusId !== "invoiced" && c.statusId !== "cancelled"
      );
      const inProgressCount = inProgress.length;
      const inProgressValue = inProgress.reduce((s, c) => s + c.total, 0);
      const avgValue = totalCount > 0 ? totalValue / totalCount : 0;

      // Mjesečni trend zadnjih 6 mjeseci.
      const now = new Date();
      const monthlyTrend = Array.from({ length: 6 }, (_, k) => {
        const monthStart = new Date(now.getFullYear(), now.getMonth() - (5 - k), 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() - (5 - k) + 1, 1);
        return aggregated.filter((c) => c.date && c.date >= monthStart && c.date < monthEnd).length;
      });

      setStats({
        totalCount,
        totalValue,
        totalSaleCount,
        totalRentCount,
        totalSaleValue,
        totalRentValue,
        avgValue,
        inProgressCount,
        inProgressValue,
        monthlyTrend,
        categories,
      });
    } catch (error) {
      if ((error as any)?.response?.status !== 401) console.error("Error fetching contract stats:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContractStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to]);

  if (isLoading) {
    return (
      <div className="relative h-full min-h-[200px]">
        <Loader isLoading={true}> </Loader>
      </div>
    );
  }

  if (stats.categories.length === 0) {
    return (
      <NoResults message={t("admin.dashboard.widget.contracts.stats.noresults")} />
    );
  }

  return (
    <div className="rounded-sm px-4 py-5 bg-white">
      <CardHeader
        title={t("admin.dashboard.widget.contracts.tab.stats.title")}
        subtitle={rangeLabel(range)}
        className="mb-10"
        right={
          <DashboardTooltip
            trigger={
              <div className="flex flex-col items-end cursor-help">
                <div className="flex items-center gap-2">
                  <Sparkline values={stats.monthlyTrend} stroke={primary.hex} fill={primary.hexSoft} />
                  <span className={`text-2xl font-bold ${text.primary}`}>
                    {formatCurrency.format(stats.totalValue)}
                  </span>
                </div>
                <span className={`text-sm ${text.muted}`}>⌀ {formatCurrency.format(stats.avgValue)} po ugovoru</span>
              </div>
            }
          >
            <TooltipTitle>Pregled ugovora</TooltipTitle>
            <TooltipRow label="Prodaja" value={`${formatCurrency.format(stats.totalSaleValue)} (${stats.totalSaleCount})`} />
            <TooltipRow label="Najam" value={`${formatCurrency.format(stats.totalRentValue)} (${stats.totalRentCount})`} />
            <TooltipDivider />
            <TooltipRow label="Ukupno" value={`${formatCurrency.format(stats.totalValue)} (${stats.totalCount})`} emphasis="positive" />
            {stats.inProgressCount > 0 && (
              <TooltipRow label="U tijeku" value={`${stats.inProgressCount} · ${formatCurrency.format(stats.inProgressValue)}`} />
            )}
          </DashboardTooltip>
        }
      />

      <div className="space-y-2">
        {stats.categories.map((category) => (
          <div
            key={category.statusId}
            className={`group/row relative rounded-sm ring-1 ring-slate-200 bg-white cursor-pointer transition-all hover:ring-2 hover:shadow-sm ${getContractStatusRingColor(category.statusId)}`}
            onClick={() => handleRowClick(category.statusId)}
          >
            {/* Ispuna proporcionalna udjelu — podaci stoje UNUTAR bara.
                overflow-hidden je na unutarnjem wrapperu da ne reže tooltip iznad reda. */}
            <div className="absolute inset-0 overflow-hidden rounded-sm">
              <div
                className={`absolute inset-y-0 left-0 origin-left animate-growRight transition-all duration-500 ${getContractStatusBarFill(category.statusId)}`}
                style={{ width: `${Math.max(category.percentage, 5)}%` }}
              />
            </div>
            <div className="relative flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate">{category.label}</div>
                <div className="text-xs text-slate-500 truncate">
                  {category.count} · {category.saleCount} prodaja / {category.rentCount} najam
                </div>
              </div>
              <div className="flex flex-col items-end flex-shrink-0">
                <span className={`text-sm font-bold ${getContractStatusAmountColor(category.statusId)}`}>{formatCurrency.format(category.total)}</span>
                <span className="text-xs text-slate-500">{category.percentage}%</span>
              </div>
            </div>
            <BarTooltip groupName="row" align="right">
              <BarTooltipTitle>{category.label}</BarTooltipTitle>
              <BarTooltipRow label="Prodaja" value={`${category.saleCount} · ${formatCurrency.format(category.saleTotal)}`} />
              <BarTooltipRow label="Najam" value={`${category.rentCount} · ${formatCurrency.format(category.rentTotal)}`} />
            </BarTooltip>
          </div>
        ))}
      </div>

      <div className="mt-4 text-sm">
        <div className={text.muted}>
          {stats.totalCount}{" "}
          {t("admin.dashboard.widget.contracts.stats.totalcontracts")}
          {" · "}
          <span className={text.subtle}>{stats.totalSaleCount} prodaja / {stats.totalRentCount} najam</span>
        </div>
        {stats.inProgressCount > 0 && (
          <div className={`${primary.text} mt-1`}>
            <span className="font-medium">{stats.inProgressCount}</span> u tijeku
            <span className={text.subtle}>{" "}({formatCurrency.format(stats.inProgressValue)})</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(ContractStatsTab);
